import { ApplicationRef, Component, NgZone, OnDestroy, OnInit, signal } from '@angular/core';
import { Subscription, fromEvent, merge } from 'rxjs';
import { LoaderService, LoaderState } from '../../services/loader.service';
import { Router, NavigationEnd } from '@angular/router';
import { debounceTime, filter, first, takeUntil } from 'rxjs/operators';
import { DonationsService } from 'src/app/services/donations.service';
import { PageLifecycleService } from 'src/app/services/page-lifecycle.service';
import { Alert } from 'src/app/models/alert.interface';
import { AlertsService } from 'src/app/services/alert.service';
import { AoAuthService, AoAuthUser } from '@ah-oh/company-auth';
import { PermissionService } from 'src/app/services/permission.service';
import dayjs from 'dayjs';
import { User } from '@sentry/angular';
import { UsersService } from 'src/app/services/users.service';
import { Subject } from 'rxjs';
import { NavLink } from '../second-header/second-header.component';
import { Socket } from 'ngx-socket-io';
import { PdfApiService } from 'src/app/services/pdf-api.service';
import { PdfJob } from 'src/app/models/pdf-job.interface';
import { environment } from 'src/environments/environment';

/**
 * Navigation bar for the fundracer site.
 * Show the site logo, navigation links, the currently logged in account,
 * the download notifications, a link to the settings and a button to log out.
 */
@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['./navbar.component.scss'],
    standalone: false,
})
export class NavbarComponent implements OnInit, OnDestroy {
    activeRequest = false;

    activeNav = false;
    oldTopPosition = 0;

    showNavigation = false;

    tasksFinished = true;

    unSeenJobs = false;
    pdfJobs = signal<PdfJob[]>([]);

    pdfJobsSkip = 0;
    pdfJobsLimit = 40;
    pdfJobsAllLoaded = false;

    pdfApiClient?: string;
    pdfApi = environment.pdfApi;

    alerts: Alert[] = [];

    pdfMenuSeen = true;

    navLinks: NavLink[] = [];
    seeSideNav = false;

    private activeDownload = false;
    private loaderSubscription?: Subscription;

    user?: (AoAuthUser & User) | undefined;

    private readonly destroy = new Subject<void>();

    constructor(
        private readonly loaderService: LoaderService,
        private readonly authService: AoAuthService,
        private readonly router: Router,
        private readonly donationsService: DonationsService,
        private readonly pageLifecycleService: PageLifecycleService,
        private readonly usersService: UsersService,
        private readonly alertsService: AlertsService,
        public readonly permission: PermissionService,
        private readonly pdfApiService: PdfApiService,
        private readonly appRef: ApplicationRef,
        private readonly zone: NgZone,
        private readonly socket: Socket,
    ) {}

    ngOnInit() {
        this.appRef.isStable.pipe(first((stable) => stable)).subscribe(() =>
            this.zone.run(() => {
                this.loaderSubscription = this.loaderService.loaderState.subscribe((res: LoaderState) => {
                    this.activeRequest = res.show;
                });

                if (this.usersService.myUser) {
                    this.user = this.usersService.myUser;
                }

                this.usersService.stable = true;

                this.setNav();
                this.getAlerts();

                this.usersService.$userChange.subscribe((user) => {
                    this.user = user;
                    this.setNav();
                    this.getAlerts();
                });

                fromEvent(window, 'scroll').subscribe(() => {
                    const newTopPosition = window.pageYOffset;
                    this.activeNav = newTopPosition < this.oldTopPosition && newTopPosition !== 0;
                    this.oldTopPosition = newTopPosition;
                });

                this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
                    this.showNavigation = false;
                });

                merge(
                    this.authService.$signIn,
                    this.donationsService.newPdfJobSub,
                    this.pageLifecycleService.visibilityChange,
                    this.pageLifecycleService.resume,
                ).subscribe(() => {
                    this.getAlerts();
                });

                this.permission.$permissionChange.subscribe(() => {
                    this.setNav();
                });

                this.donationsService.tasksFinishedSub
                    .pipe(takeUntil(this.destroy), debounceTime(500))
                    .subscribe((res: boolean) => {
                        this.tasksFinished = res;
                    });

                this.donationsService.newPdfJobSub.pipe(takeUntil(this.destroy)).subscribe(() => {
                    this.pdfMenuSeen = false;
                    this.reloadPdfJobs();
                });

                this.socket
                    .fromEvent('new-pdf')
                    .pipe(takeUntil(this.destroy))
                    .subscribe(() => {
                        this.pdfMenuSeen = false;
                        this.reloadPdfJobs();
                    });

                this.reloadPdfJobs();
            }),
        );
    }

    ngOnDestroy(): void {
        this.loaderSubscription?.unsubscribe();
        this.destroy.next();
        this.destroy.complete();
    }

    get isSignedIn(): boolean {
        return this.authService.isSignedIn;
    }

    getAlerts(): void {
        if (this.authService.isSignedIn) {
            this.alertsService.find().subscribe((res: Alert[]) => {
                this.alerts = res;
            });
        }
    }

    reloadPdfJobs(): void {
        this.pdfJobsSkip = 0;
        this.pdfJobsAllLoaded = false;
        this.loadMorePdfJobs();
    }

    loadMorePdfJobs(): void {
        const isInitial = this.pdfJobsSkip === 0;
        if (this.pdfJobsAllLoaded) return;
        this.pdfApiService
            .findAllPdfJobs({
                limit: this.pdfJobsLimit,
                skip: this.pdfJobsSkip,
            })
            .subscribe((pdfJobs) => {
                this.pdfJobs.update((jobs) => (isInitial ? pdfJobs : [...jobs, ...pdfJobs]));
                if (this.pdfJobs().length === 0) {
                    this.pdfJobsAllLoaded = true;
                }
                this.pdfJobsSkip = this.pdfJobs().length;
            });
    }

    pdfJobMenuScrolled() {
        console.log('scroll');
        this.loadMorePdfJobs();
    }

    downloadPdf(job: PdfJob & { isOld?: boolean }): void {
        if (job.isOld) {
            return; // no longer supported, see https://github.com/ah-oh/fundracer/issues/1568#issuecomment-4089216297
        }

        if (this.activeDownload) {
            return;
        }

        this.activeDownload = true;

        this.pdfApiService.downloadPdf({ jobId: job._id }).subscribe(
            (res) => {
                const downloadLink = document.createElement('a');
                downloadLink.href = window.URL.createObjectURL(res.body);
                downloadLink.setAttribute(
                    'download',
                    `fundracer-${dayjs(job.createdAt).format('DD.MM.YYYY-HH:mm:ss')}.pdf`,
                );
                document.body.appendChild(downloadLink);
                downloadLink.click();
                this.activeDownload = false;
            },
            () => {
                this.activeDownload = false;
            },
        );
    }

    deletePdf(job: PdfJob): void {
        this.pdfApiService.delete({ jobId: job._id }).subscribe(() => {
            this.loadMorePdfJobs();
        });
    }

    onAlertHide(alert: Alert): void {
        this.alertsService.update({ id: alert._id, dto: { hide: true } }).subscribe(() => {
            this.alerts.splice(this.alerts.indexOf(alert), 1);
        });
    }

    signOut(): void {
        sessionStorage.removeItem('filter');
        this.authService.signOut().subscribe(() => {
            this.router.navigate(['/auth/login']);
        });
    }

    private setNav(): void {
        this.navLinks = [];

        if (this.permission.DONATION_ENTRANCE) {
            this.navLinks.push({ label: 'Spendeneingang', path: '/dashboard', exact: true });
        }
        if (this.permission.DONATORS) {
            this.navLinks.push({ label: 'Spender', path: '/spender/uebersicht', exact: true });
        }
        if (this.permission.PROJECTS) {
            this.navLinks.push({ label: 'Spendenaktionen', path: '/aktionen', exact: false });
        }
        if (this.permission.SETTINGS) {
            this.navLinks.push({ label: 'Dokumente', path: '/einstellungen/dokumente', exact: false });
        }
        if (this.permission.EVALUATION) {
            this.navLinks.push({ label: 'Auswertungen', path: '/auswertungen', exact: false });
        }
    }
}
